<script setup lang="ts">
import { computed } from 'vue';
import { useMessageActionsCustomisation } from './composables/useMessageActionsCustomisation';

export interface ActionItem {
  id: string | number;
  label?: string;
  icon?: string;
  selected?: boolean;
  disabled?: boolean;
  rightIcon?: string;
  imageUri?: string;
}

export interface MessageActionsProps {
  /**
   * List of action items to render
   */
  items?: ActionItem[];
  /**
   * Variant of the actions container
   * - 'categories': Standard category list with background
   * - 'tree': Tree-style list for sections/articles
   */
  variant?: 'categories' | 'tree';
  /**
   * Additional CSS classes for the container
   */
  containerClass?: string;
}

const props = withDefaults(defineProps<MessageActionsProps>(), {
  items: () => [],
  variant: 'categories',
  containerClass: '',
});

const emit = defineEmits<{
  (e: 'select', item: ActionItem): void;
}>();

// Customisation
const customisation = useMessageActionsCustomisation();

const containerClass = computed(() => ({
  'action-categories': props.variant === 'categories',
  'action-tree': props.variant === 'tree',
}));

const handleClick = (item: ActionItem) => {
  if (item.disabled) return;
  emit('select', item);
};

// Function to compute styles per item (handles selected state)
const getItemStyle = (item: ActionItem) => {
  const textColor = customisation.suggestionsTextColor.value;
  const backgroundColor = customisation.suggestionsBackgroundColor.value;
  const borderColor = item.selected
    ? customisation.suggestionsSelectedBorderColor.value
    : customisation.suggestionsBorderColor.value;

  return {
    ...(textColor ? { color: textColor } : {}),
    ...(backgroundColor ? { backgroundColor } : {}),
    ...(borderColor ? { border: `1px solid ${borderColor}` } : {}),
  };
};
</script>

<template>
  <div :class="[containerClass, props.containerClass]">
    <slot>
      <div
        v-for="item in items"
        :key="item.id"
        class="btn-radius item"
        :class="{ 'selected-item': item.selected, disabled: item.disabled }"
        :style="getItemStyle(item)"
        data-testid="message-action-item"
        :data-action-id="item.id"
        @click="handleClick(item)"
      >
        <img v-if="item.imageUri" :src="item.imageUri" alt="" class="item-icon" />
        <v-icon v-if="item.icon" :name="item.icon" class="item-icon" />
        <span v-if="item.label" class="item-text theme-font-main">{{ item.label }}</span>
        <span v-if="item.rightIcon" class="link-icon">
          <v-icon :name="item.rightIcon" />
        </span>
      </div>
    </slot>
  </div>
</template>

<style scoped>
.action-categories,
.action-tree {
  display: flex;
  flex-direction: column;
}

.item {
  padding: 10px 0;
  display: flex;
  align-items: center;
  margin-bottom: 12px;
  cursor: pointer;
}

.action-categories .item {
  display: flex;
  align-items: center;
}

.item-icon {
  width: 16px;
  height: 16px;
  margin-left: 12px;
}

.item .item-text {
  flex: 1;
  padding: 0 12px;
}

.item .link-icon {
  margin-right: 8px;
}

.item.selected-item {
  background-color: transparent;
  position: relative;
}

.item.selected-item .link-icon {
  opacity: 0;
}

.item.disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.action-tree .item::first-child {
  margin-top: 10px;
}

.action-tree .item {
  cursor: pointer;
  min-width: 184px;
}
</style>
